/**
 * @module events/listeners/emitters
 */

/**
 * @typedef {{}} Options
 * @property {function[]} hooks
 * @property {?Boolean|false} autoClose
 * @property {?Boolean|false} capture
 * @property {?Boolean|false} passive
 */

/**
 * @typedef {function} Listener
 * @param {EventTarget} currentTarget The EventTarget to listen
 * @param {?Promise<*>} aborted The abortion promise
 * @returns {Promise<?Event>}
 */

/**
 * @typedef {function} Generator
 * @param {EventTarget} currentTarget The EventTarget to listen
 * @param {?Promise<*>} aborted The abortion promise
 * @returns {AsyncGenerator<Event>}
 */

import array from '../../utils/array.js'
import emitter from '../../utils/emitter.js'
import never from '../../utils/never.js'
import object from '../../utils/object.js'
import unresolved from '../../utils/unresolved.js'
import prototype from '../listeners/listener.js'
import once from '../options/once.js'

const init = (
  type,
  autoClose,
  hooks,
  options
) => {
  const emitters = new WeakMap()
  const iterations = new WeakMap()
  const listeners = new WeakMap()
  const { once } = options

  const listener = object(prototype, {
    ...options,
    hooks,
    type: array([type].flat()),
    task: (originalTarget, event) => {
      const emitter = emitters.get(originalTarget)

      queueMicrotask(async () => {
        if (!emitter) {
          return
        }

        await emitter.emit(event)

        if (once) {
          await iterations.get(emitter)

          for (const listener of listeners.get(originalTarget)) {
            listener.return()
          }
        }
      })
    }
  })

  return (
    originalTarget,
    aborted
  ) => {
    if (!emitters.has(originalTarget)) {
      const current = emitter(autoClose)
      const { closed } = current

      emitters.set(originalTarget, current)
      listeners.set(originalTarget, [])
      listener.listen(originalTarget)

      closed.then(() => {
        listener.forget(originalTarget)
        emitters.delete(originalTarget)
        listeners.delete(originalTarget)
      })
    }

    const source = emitters.get(originalTarget)
    const current = emitters.get(originalTarget).listen()

    listeners.get(originalTarget).push(current)

    aborted.then(() => {
      const index = listeners.get(originalTarget).indexOf(current)

      if (index !== -1) {
        listeners.get(originalTarget).splice(index, 1)
      }

      current.return()
    })

    return listen(current, aborted, iterations, source)
  }
}

const listen = async function* (listener, aborted, iterations, emitter) {
  try {
    while (true) {
      const iteration = listener.next()

      iterations.set(emitter, iteration)

      const event = (await iteration).value

      if (!await unresolved(aborted)) {
        break
      }

      yield event
    }
  } finally {
    iterations.delete(emitter)
    listener.return()
  }
}

/**
 * Listen an event once
 * @param {string|string[]} type
 * @param {?Options} options
 * @returns {Listener}
 */
export const event = (
  type,
  {
    hooks: [...hooks] = [],
    autoClose = false,
    capture = false,
    passive = false
  } = {}
) => {
  const event = init(type, autoClose, hooks, { capture, passive, once })

  return async (
    currentTarget,
    aborted = never
  ) => (await event(currentTarget, aborted).next()).value
}

/**
 * Listen any events
 * @param {string|string[]} type
 * @param {?Options} options
 * @returns {Generator}
 */
export const events = (
  type,
  {
    hooks: [...hooks] = [],
    autoClose = false,
    capture = false,
    passive = false
  } = {}
) => {
  const events = init(type, autoClose, hooks, { capture, passive })

  return (
    currentTarget,
    aborted = never
  ) => events(currentTarget, aborted)
}
